/**
 * «Невозвраты» (`/nonreturn`): вещи проката, которые клиент не вернул в срок. Единица — вещь сделки
 * (строка договора «прокат», выдана, возврата нет), месяц — месяц конца проката. По умолчанию —
 * платья за 12 месяцев; `?all=1` — все вещи, `?tab=cities` — города и матрица город × месяц,
 * `?tab=notice` — «Салоны»: сообщения руководителям в «Head <салон>» и их ответы
 * (`docs/specs/2026-10-23-nonreturn-notice-design.md`).
 * Клик по числу — список сделок ниже; клик по номеру сделки — панель справа (`?deal=&chat=`):
 * переписка клиента и сделка, без ухода с экрана. Спека `docs/specs/2026-10-08-nonreturn-design.md`; числа —
 * те же, что у `prokat-runner nonreturn-report`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { DealSide, type DealSideTab } from "@/components/DealSide";
import {
  type City,
  type Count,
  type Deal,
  type Kind,
  loadNonreturn,
  loadNonreturnList,
  loadNonreturnNotice,
  type NonreturnReport as Data,
  type NoticeRow,
  type NoticeState,
  type Numbers,
} from "@/lib/nonreturn";
import { card, Num, td, th } from "@/pages/AppointmentsUi";

type Tab = "months" | "cities" | "notice";
type Metric = "open" | "late14" | "late3" | "share";
type Pick = { kind: Kind; from?: string | undefined; to?: string | undefined; city?: string | undefined; title: string };

const TABS: { id: Tab; label: string }[] = [
  { id: "months", label: "по месяцам" },
  { id: "cities", label: "по городам" },
  { id: "notice", label: "салоны" },
];
const METRICS: { id: Metric; label: string }[] = [
  { id: "open", label: "не вернули" },
  { id: "late14", label: "опоздали > 14 дн." },
  { id: "late3", label: "опоздали > 3 дн." },
  { id: "share", label: "доля проблемных" },
];
const NO_SIDE = { deal: undefined, chat: undefined, side: undefined };
const NO_PICK = { pk: undefined, pf: undefined, pt: undefined, pc: undefined, pn: undefined, ...NO_SIDE };
const pill = (on: boolean) =>
  `rounded-full border px-2.5 py-0.5 text-[12px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;

/** Проблемные — не вернули или вернули позже срока больше чем на 14 дней. */
const bad = (n: Numbers) => n.open + n.late14;
const share = (n: Numbers) => (n.ended ? `${((bad(n) * 100) / n.ended).toFixed(2).replace(".", ",")}%` : "—");
const dm = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "—");
const things = (dress: boolean, n: number) => `${n} ${dress ? "плат." : "вещ."}`;

/** Границы месяца `2026-09` для отбора списка по концу проката. */
function bounds(key: string): { from: string; to: string } {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const last = new Date(y, m, 0).getDate();
  return { from: `${key}-01`, to: `${key}-${String(last).padStart(2, "0")}` };
}

/** Кнопки периода: 12 месяцев и годы копии целиком. */
function periods(today: string): { label: string; from?: string; to?: string }[] {
  const year = Number(today.slice(0, 4));
  const years = [];
  for (let y = year; y >= 2024; y--) years.push({ label: String(y), from: `${y}-01`, to: y === year ? today.slice(0, 7) : `${y}-12` });
  return [{ label: "12 месяцев" }, ...years, { label: "с 2024", from: "2024-01", to: today.slice(0, 7) }];
}

function NowTile({ label, n, dress, hint, on, active }: { label: string; n: Count; dress: boolean; hint: string; on: () => void; active: boolean }) {
  return (
    <button
      type="button"
      onClick={on}
      disabled={!n.items}
      className={`${card} flex min-w-0 flex-col gap-1 p-4 text-left enabled:hover:border-border-strong ${active ? "border-accent" : ""}`}
      data-annot={`плитка «${label}»`}
    >
      <span className="text-[12px] text-fg-muted">{label}</span>
      <span className="text-[22px] font-semibold tabular-nums tracking-[-0.02em]">{n.items}</span>
      <span className="text-[11.5px] text-fg-faint">
        {dress ? "платьев" : "вещей"} · в {n.deals} сделках · {hint}
      </span>
    </button>
  );
}

/** Столбики по месяцам: не вернули (красный) поверх опоздавших больше 14 дней (жёлтый). */
function Bars({ d }: { d: Data }) {
  const max = Math.max(1, ...d.months.map(bad));
  return (
    <section className={card} data-annot="столбики невозвратов по месяцам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Проблемные возвраты по месяцу конца проката</h2>
        <span className="flex items-center gap-1.5 text-[12px] text-fg-faint">
          <i className="inline-block size-2.5 rounded-sm bg-err" /> не вернули до сих пор
          <i className="ml-2 inline-block size-2.5 rounded-sm bg-warn" /> вернули с опозданием больше {d.late_days[1]} дней
        </span>
      </div>
      <div className="flex items-end gap-1 overflow-x-auto px-4 pb-2 pt-4">
        {d.months.map((m) => (
          <div key={m.key} className="flex min-w-7 flex-1 flex-col items-center gap-1" title={`${m.label}: не вернули ${m.open}, опоздали > ${d.late_days[1]} дн. ${m.late14}, закончилось ${m.ended}`}>
            <span className="text-[11px] tabular-nums text-fg-muted">{bad(m) || ""}</span>
            <div className="flex h-28 w-full max-w-9 flex-col justify-end overflow-hidden rounded-sm">
              <div className="bg-err" style={{ height: `${(m.open / max) * 100}%` }} />
              <div className="bg-warn" style={{ height: `${(m.late14 / max) * 100}%` }} />
            </div>
            <span className="whitespace-nowrap text-[10.5px] text-fg-faint">{m.label.slice(0, 3)}</span>
            <span className="text-[10.5px] tabular-nums text-fg-faint">{m.key.slice(5) === "01" || m === d.months[0] ? m.key.slice(0, 4) : "\u00a0"}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Months({ d, pick, setPick }: { d: Data; pick: Pick | null; setPick: (p: Pick) => void }) {
  const is = (p: Pick) => pick?.kind === p.kind && pick.from === p.from && pick.to === p.to && pick.city === p.city;
  const where = d.city ? ` · ${d.city}` : "";
  const cell = (kind: Kind, n: number, label: string, title: string, range: { from?: string; to?: string }) => {
    const p: Pick = { kind, ...range, city: d.city ?? undefined, title: `${label} · ${title}${where}` };
    return <Num n={n} active={is(p)} on={() => setPick(p)} />;
  };
  const row = (key: string, label: string, n: Numbers, range: { from?: string; to?: string }, strong = false) => (
    <tr key={key} className={`border-t border-border hover:bg-bg-hover ${strong ? "font-medium" : ""}`} data-annot={`невозвраты ${key}`}>
      <td className="whitespace-nowrap px-4 py-1.5">{label}</td>
      <td className={`${td} text-fg-muted`}>{n.ended || "·"}</td>
      <td className={td}>{cell("open", n.open, label, "не вернули", range)}</td>
      <td className={`${td} text-fg-muted`}>{n.open_deals || "·"}</td>
      <td className={td}>{cell("late3", n.late3, label, `опоздали > ${d.late_days[0]} дн.`, range)}</td>
      <td className={td}>{cell("late14", n.late14, label, `опоздали > ${d.late_days[1]} дн.`, range)}</td>
      <td className={`${td} text-fg-muted`}>{share(n)}</td>
    </tr>
  );
  const first = bounds(d.from).from;
  const before = new Date(`${first}T00:00:00`);
  before.setDate(before.getDate() - 1);
  return (
    <section className={card} data-annot="невозвраты по месяцам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">По месяцам</h2>
        <span className="text-[12px] text-fg-faint">
          месяц конца проката по договору; опоздали — вернули позже срока; доля — не вернули и опоздавшие больше{" "}
          {d.late_days[1]} дней от закончившихся прокатов
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Месяц</th>
              <th className={th}>Закончилось прокатов</th>
              <th className={th}>Не вернули</th>
              <th className={th}>сделок</th>
              <th className={th}>Опоздали &gt; {d.late_days[0]} дн.</th>
              <th className={th}>из них &gt; {d.late_days[1]} дн.</th>
              <th className={th}>Доля проблемных</th>
            </tr>
          </thead>
          <tbody>
            {[...d.months].reverse().map((m) => row(m.key, m.label, m, bounds(m.key)))}
            {row("total", "Итого за период", d.total, { from: first, to: bounds(d.to).to }, true)}
            {d.before.items > 0 && (
              <tr className="border-t border-border hover:bg-bg-hover" data-annot="невозвраты раньше периода">
                <td className="whitespace-nowrap px-4 py-1.5 text-fg-muted">Раньше периода</td>
                <td className={td} />
                <td className={td}>{cell("open", d.before.items, "раньше периода", "не вернули", { to: before.toLocaleDateString("sv-SE") })}</td>
                <td className={`${td} text-fg-muted`}>{d.before.deals}</td>
                <td className={td} colSpan={3} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Cities({ d, metric, setMetric, pick, setPick }: { d: Data; metric: Metric; setMetric: (m: Metric) => void; pick: Pick | null; setPick: (p: Pick) => void }) {
  const is = (p: Pick) => pick?.kind === p.kind && pick.from === p.from && pick.to === p.to && pick.city === p.city;
  const period = { from: bounds(d.from).from, to: bounds(d.to).to };
  const cities = d.cities.filter((c) => c.ended || c.now.items);
  const value = (n: Numbers | undefined) => (!n ? 0 : metric === "share" ? (n.ended ? (bad(n) * 100) / n.ended : 0) : n[metric]);
  const max = Math.max(1e-9, ...cities.flatMap((c) => d.months.map((m) => value(c.months[m.key]))));
  const num = (c: City, kind: Kind, n: number, title: string, range: { from?: string; to?: string }) => {
    const p: Pick = { kind, ...range, city: c.code, title: `${c.city} · ${title}` };
    return <Num n={n} active={is(p)} on={() => setPick(p)} />;
  };
  return (
    <>
      <section className={card} data-annot="невозвраты по городам">
        <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
          <h2 className="text-[13px] font-medium">По городам</h2>
          <span className="text-[12px] text-fg-faint">город салона договора; «сейчас» — всё, что на руках сверх срока, за любое время; остальное — за период</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Город</th>
                <th className={th}>Невозврат сейчас</th>
                <th className={th}>сделок</th>
                <th className={th}>Закончилось прокатов</th>
                <th className={th}>Не вернули за период</th>
                <th className={th}>Опоздали &gt; {d.late_days[0]} дн.</th>
                <th className={th}>из них &gt; {d.late_days[1]} дн.</th>
                <th className={th}>Доля проблемных</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => (
                <tr key={c.code} className="border-t border-border hover:bg-bg-hover" data-annot={`город ${c.city}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">{c.city}</td>
                  <td className={`${td} font-medium`}>{num(c, "open", c.now.items, "невозврат сейчас", {})}</td>
                  <td className={`${td} text-fg-muted`}>{c.now.deals || "·"}</td>
                  <td className={`${td} text-fg-muted`}>{c.ended || "·"}</td>
                  <td className={td}>{num(c, "open", c.open, "не вернули за период", period)}</td>
                  <td className={td}>{num(c, "late3", c.late3, `опоздали > ${d.late_days[0]} дн.`, period)}</td>
                  <td className={td}>{num(c, "late14", c.late14, `опоздали > ${d.late_days[1]} дн.`, period)}</td>
                  <td className={`${td} text-fg-muted`}>{share(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className={card} data-annot="матрица город × месяц">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
          <h2 className="text-[13px] font-medium">Город × месяц</h2>
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Показатель">
            {METRICS.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={metric === m.id} onClick={() => setMetric(m.id)} className={pill(metric === m.id)}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="sticky left-0 bg-bg-subtle px-4 py-1.5 text-left font-medium">Город</th>
                {d.months.map((m) => (
                  <th key={m.key} className="whitespace-nowrap px-2 py-1.5 text-right font-medium">
                    {m.label.slice(0, 3)} {m.key.slice(2, 4)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cities.map((c) => (
                <tr key={c.code} className="border-t border-border">
                  <td className="sticky left-0 whitespace-nowrap bg-bg-subtle px-4 py-1.5">{c.city}</td>
                  {d.months.map((m) => {
                    const n = c.months[m.key];
                    const v = value(n);
                    const heat = v ? Math.round(8 + (v / max) * 42) : 0;
                    const kind: Kind = metric === "late3" ? "late3" : metric === "late14" ? "late14" : "open";
                    const p: Pick = { kind, ...bounds(m.key), city: c.code, title: `${c.city} · ${m.label} · ${METRICS.find((x) => x.id === (metric === "share" ? "open" : metric))!.label}` };
                    return (
                      <td
                        key={m.key}
                        className="px-2 py-1.5 text-right tabular-nums"
                        style={heat ? { background: `color-mix(in srgb, var(--err) ${heat}%, transparent)` } : undefined}
                        title={n ? `закончилось ${n.ended}, не вернули ${n.open}, опоздали > ${d.late_days[1]} дн. ${n.late14}` : undefined}
                      >
                        {!n || !v ? (
                          <span className="text-fg-faint">·</span>
                        ) : metric === "share" ? (
                          share(n)
                        ) : (
                          <Num n={v} active={is(p)} on={() => setPick(p)} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** Что не вернули по сделке — над блоком сделки в панели справа. */
function Owed({ x, late }: { x: Deal; late: boolean }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]" data-annot="что не вернули">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">
          Прокат {dm(x.rent_start)} — {dm(x.rent_end)}
        </span>
        {x.days !== null && x.days > 0 && <span className={late ? "text-fg-muted" : "text-err"}>{late ? `опоздали на ${x.days} дн.` : `просрочка ${x.days} дн.`}</span>}
        <span className="text-fg-muted">· {x.city || "без города"}</span>
      </div>
      <ul className="mt-1.5 flex flex-col gap-0.5">
        {x.items.map((i) => (
          <li key={i.serial} className={i.dress ? "" : "text-fg-muted"}>
            {i.name} · {i.serial}
            <span className="text-fg-faint">
              {" "}
              — выдали {dm(i.issued_at)}, {i.returned_at ? `вернули ${dm(i.returned_at)}` : "не вернули"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type Side = { deal: number | null; chat: number | null; tab: DealSideTab; open: (x: Deal) => void; openId: (deal: number) => void; set: (next: { chat?: number; tab?: DealSideTab }) => void; close: () => void };

function Deals({ pick, all, side }: { pick: Pick; all: boolean; side: Side }) {
  const q = useQuery({
    queryKey: ["nonreturn-list", pick.kind, pick.from, pick.to, pick.city, all],
    queryFn: () => loadNonreturnList(pick.kind, { from: pick.from, to: pick.to, city: pick.city, all }),
  });
  const late = pick.kind === "late3" || pick.kind === "late14";
  const opened = q.data?.deals.find((x) => x.deal === side.deal);
  return (
    <section className={card} data-annot="сделки невозврата">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{pick.title}</h2>
        {q.data && (
          <span className="text-[12px] text-fg-faint">
            {q.data.total} сделок · {things(!all, q.data.items)}
            {q.data.total > q.data.limit ? ` · показаны первые ${q.data.limit}` : ""}
          </span>
        )}
      </div>
      {q.isPending && <p className="px-4 py-3 text-[12.5px] text-fg-muted">Загружаю…</p>}
      {q.error && <p className="px-4 py-3 text-[12.5px] text-err">{String(q.error)}</p>}
      {q.data && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Конец проката</th>
                <th className={th}>{late ? "Опоздали, дн." : "Просрочка, дн."}</th>
                <th className="px-3 py-1.5 text-left font-medium">Город</th>
                <th className="px-3 py-1.5 text-left font-medium">Сделка</th>
                <th className="px-3 py-1.5 text-left font-medium">Стадия</th>
                <th className="px-3 py-1.5 text-left font-medium">Клиент</th>
                <th className="px-3 py-1.5 text-left font-medium">Вещи</th>
              </tr>
            </thead>
            <tbody>
              {q.data.deals.map((x) => (
                <tr key={x.deal} className={`border-t border-border align-top hover:bg-bg-hover ${x.deal === side.deal ? "bg-accent/10" : ""}`}>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{dm(x.rent_end)}</td>
                  <td className={`${td} ${!late && (x.days ?? 0) > 30 ? "font-medium text-err" : ""}`}>{x.days ?? "·"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{x.city || "—"}</td>
                  <td className="px-3 py-1.5 font-mono">
                    {/* Обычный клик — панель справа; Ctrl/средняя кнопка — карточка в новой вкладке. */}
                    <Link
                      to={`/deals/${x.deal}`}
                      className="underline"
                      title="сделка и переписка — в панели справа"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        side.open(x);
                      }}
                    >
                      {x.deal}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {x.stage_name || "—"}
                    {!late && x.closed && <span className="ml-1.5 rounded bg-err-bg px-1 text-[11px] text-err">закрыта без возврата</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    {x.customer || "—"}
                    {x.phone && (
                      <Link to={`/search?q=${x.phone}`} className="ml-2 font-mono text-fg-muted hover:underline" title="поиск по номеру">
                        {x.phone}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {x.items.map((i) => (
                      <div key={i.serial} className={i.dress ? "" : "text-fg-muted"}>
                        {i.name} · {i.serial}
                        {i.returned_at && <span className="text-fg-faint"> — вернули {dm(i.returned_at)}</span>}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {side.deal && (
        <DealSide
          dealId={side.deal}
          chatId={side.chat ?? opened?.chat?.id ?? null}
          tab={side.tab}
          title={
            <>
              Сделка {side.deal}
              {opened?.customer && <span className="font-normal text-fg-muted"> · {opened.customer}</span>}
              <Link to={`/deals/${side.deal}`} className="ml-3 text-[12px] font-normal text-fg-muted underline" title="вся карточка сделки">
                карточка
              </Link>
            </>
          }
          onTab={(tab) => side.set({ tab })}
          onChat={(chat) => side.set({ chat })}
          onClose={side.close}
        >
          {opened && <Owed x={opened} late={late} />}
        </DealSide>
      )}
    </section>
  );
}

const STATE: Record<NoticeState, { label: string; cls: string }> = {
  sent: { label: "ждём ответа", cls: "bg-warn-bg text-warn" },
  answered: { label: "ответили", cls: "bg-accent/10 text-fg" },
  returned: { label: "вернули", cls: "bg-ok-bg text-ok" },
  failed: { label: "не ушло", cls: "bg-err-bg text-err" },
};
type Step = "all" | "waiting" | "answered" | "asked" | "returned";
const dt = (iso: string | null) => (iso ? `${dm(iso)} ${iso.slice(11, 16)}` : "—");

/** «Салоны»: воронка сообщений руководителям и цепочка по сделке; клик по шагу воронки — отбор списка. */
function Notice({ side }: { side: Side }) {
  const q = useQuery({ queryKey: ["nonreturn-notice"], queryFn: loadNonreturnNotice });
  const [params, setParams] = useSearchParams();
  const step = (params.get("step") as Step | null) ?? "all";
  const setStep = (s: Step) => {
    const out = new URLSearchParams(params);
    if (s === "all") out.delete("step");
    else out.set("step", s);
    setParams(out, { replace: true });
  };
  if (q.isPending) return <p className="text-[12.5px] text-fg-muted">Загружаю…</p>;
  if (q.error) return <p className="text-[12.5px] text-err">{String(q.error)}</p>;
  const { funnel: f, rows, pending } = q.data;
  const fits = (r: NoticeRow) =>
    step === "all" ||
    (step === "waiting" && r.state === "sent") ||
    (step === "answered" && r.answers > 0) ||
    (step === "asked" && r.asks > 0) ||
    (step === "returned" && r.state === "returned");
  const steps: { id: Step; label: string; n: number; hint: string }[] = [
    { id: "all", label: "Написали", n: f.sent, hint: "сообщение по сделке ушло в «Head <салон>»" },
    { id: "answered", label: "Ответили", n: f.answered, hint: "руководитель ответил реплаем — ответ в комментарии сделки" },
    { id: "asked", label: "Переспрашивали", n: f.asked, hint: "2 суток без ответа или без возврата — бот спросил статус" },
    { id: "waiting", label: "Ждём ответа", n: f.waiting, hint: "последний вопрос без ответа" },
    { id: "returned", label: "Вернули", n: f.returned, hint: "в CRM у сделки не осталось вещей на руках" },
  ];
  const shown = rows.filter(fits);
  const opened = rows.find((r) => r.deal === side.deal);
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6" data-annot="воронка сообщений салонам">
        <div className={`${card} px-4 py-3`} title="вещь на руках, прокат кончился за последние 30 дней">
          <div className="text-[12px] text-fg-muted">Невозвратов за 30 дней</div>
          <div className="text-[22px] font-semibold tabular-nums">{f.overdue}</div>
          {pending.length > 0 && <div className="text-[11.5px] text-fg-faint">ещё не писали: {pending.length}</div>}
        </div>
        {steps.map((s) => (
          <button key={s.id} type="button" title={s.hint} onClick={() => setStep(s.id)} className={`${card} px-4 py-3 text-left ${step === s.id ? "ring-1 ring-accent" : ""}`}>
            <div className="text-[12px] text-fg-muted">{s.label}</div>
            <div className="text-[22px] font-semibold tabular-nums">{s.n}</div>
          </button>
        ))}
      </div>
      <p className="text-[12px] text-fg-faint">
        По каждой сделке с невозвратом за последние 30 дней бот пишет в «Head &lt;салон&gt;» и отмечает руководителя; ответ реплаем ложится комментарием в сделку.
        Нет ответа 2 суток, а вещь не вернули — бот переспрашивает. Новые сообщения и переспросы — с 10 до 20 по времени салона.
      </p>
      <section className={card} data-annot="цепочки по сделкам">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Сделка</th>
                <th className="px-3 py-1.5 text-left font-medium">Город</th>
                <th className="px-3 py-1.5 text-left font-medium">Стадия</th>
                <th className="px-3 py-1.5 text-left font-medium">Конец проката</th>
                <th className="px-3 py-1.5 text-left font-medium">Написали</th>
                <th className={th}>Переспросов</th>
                <th className="px-3 py-1.5 text-left font-medium">Паспорт</th>
                <th className="px-3 py-1.5 text-left font-medium">Последний ответ</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-3 text-fg-muted">
                    Сообщений пока нет
                  </td>
                </tr>
              )}
              {shown.map((r) => (
                <tr key={`${r.deal}-${r.group}`} className={`border-t border-border align-top hover:bg-bg-hover ${r.deal === side.deal ? "bg-accent/10" : ""}`}>
                  <td className="px-4 py-1.5 font-mono">
                    <Link
                      to={`/deals/${r.deal}`}
                      className="underline"
                      title="сделка и переписка — в панели справа"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        side.openId(r.deal);
                      }}
                    >
                      {r.deal}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{r.city || r.group}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[11.5px] ${STATE[r.state].cls}`} title={r.error || undefined}>
                      {STATE[r.state].label}
                    </span>
                    {r.returned && <span className="ml-1.5 text-fg-faint">{dm(r.returned)}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{dm(r.rent_end)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{dt(r.sent)}</td>
                  <td className={td}>{r.asks || "·"}</td>
                  <td className={`whitespace-nowrap px-3 py-1.5 ${r.passport ? "" : "text-err"}`}>{r.passport ? "есть" : "нет"}</td>
                  <td className="px-3 py-1.5">
                    {r.reply ? (
                      <>
                        <span className="text-fg-faint">
                          {dt(r.reply.at)} · {r.reply.name}
                          {!r.reply.comment && " · ещё не в сделке"}:
                        </span>{" "}
                        {r.reply.text}
                      </>
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {side.deal && (
          <DealSide
            dealId={side.deal}
            chatId={side.chat}
            tab={side.tab}
            title={
              <>
                Сделка {side.deal}
                {opened && <span className="font-normal text-fg-muted"> · {STATE[opened.state].label}</span>}
                <Link to={`/deals/${side.deal}`} className="ml-3 text-[12px] font-normal text-fg-muted underline" title="вся карточка сделки">
                  карточка
                </Link>
              </>
            }
            onTab={(tab) => side.set({ tab })}
            onChat={(chat) => side.set({ chat })}
            onClose={side.close}
          />
        )}
      </section>
    </>
  );
}

export function NonreturnPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "cities" ? "cities" : params.get("tab") === "notice" ? "notice" : "months";
  const all = params.get("all") === "1";
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const city = (tab === "months" && params.get("city")) || undefined;
  const metric = METRICS.find((m) => m.id === params.get("metric"))?.id ?? "open";
  const set = (next: Record<string, string | undefined>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["nonreturn", from, to, city, all], queryFn: () => loadNonreturn({ from, to, city, all }) });
  const d = q.data;
  const pk = params.get("pk") as Kind | null;
  const pick: Pick | null = pk
    ? { kind: pk, from: params.get("pf") ?? undefined, to: params.get("pt") ?? undefined, city: params.get("pc") ?? undefined, title: params.get("pn") ?? "" }
    : null;
  // Город «без города» — пустая строка: в адресе её держит `pc=-`.
  const setPick = (p: Pick) => set({ ...NO_SIDE, pk: p.kind, pf: p.from, pt: p.to, pc: p.city === "" ? "-" : p.city, pn: p.title });
  const shown: Pick | null = pick && { ...pick, city: pick.city === "-" ? "" : pick.city };
  const side: Side = {
    deal: Number(params.get("deal")) || null,
    chat: Number(params.get("chat")) || null,
    tab: params.get("side") === "chat" ? "chat" : "deal",
    open: (x) => set({ deal: String(x.deal), chat: x.chat ? String(x.chat.id) : undefined, side: undefined }),
    openId: (deal) => set({ deal: String(deal), chat: undefined, side: undefined }),
    set: (next) => set({ ...(next.chat ? { chat: String(next.chat) } : {}), ...(next.tab ? { side: next.tab === "chat" ? "chat" : undefined } : {}) }),
    close: () => set(NO_SIDE),
  };
  const now = (kind: Kind, title: string): Pick => ({ kind, city, title: `${title}${city ? ` · ${city}` : ""}` });
  const isNow = (kind: Kind) => pick?.kind === kind && !pick.from && !pick.to && (pick.city ?? undefined) === city;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-col gap-2.5" data-annot="шапка невозвратов">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Невозвраты</h1>
          <div className="flex gap-1" role="radiogroup" aria-label="Вид">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={tab === t.id} onClick={() => set({ ...NO_PICK, step: undefined, tab: t.id === "months" ? undefined : t.id })} className={pill(tab === t.id)}>
                {t.label}
              </button>
            ))}
          </div>
          {tab !== "notice" && (
          <div className="flex gap-1" role="radiogroup" aria-label="Что считаем" data-annot="платья или все вещи">
            <button type="button" role="radio" aria-checked={!all} onClick={() => set({ ...NO_PICK, all: undefined })} className={pill(!all)}>
              платья
            </button>
            <button type="button" role="radio" aria-checked={all} onClick={() => set({ ...NO_PICK, all: "1" })} className={pill(all)} title="платья, аксессуары, мужские костюмы, обувь">
              все вещи
            </button>
          </div>
          )}
          {tab === "months" && d && (
            <select
              value={city ?? ""}
              onChange={(e) => set({ ...NO_PICK, city: e.target.value || undefined })}
              className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg"
              data-annot="выбор города"
            >
              <option value="">все города</option>
              {d.cities.filter((c) => c.code).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.city}
                </option>
              ))}
            </select>
          )}
        </div>
        {tab !== "notice" && (
        <div className="flex flex-wrap items-center gap-1" data-annot="выбор периода">
          <span className="mr-1 text-[12px] text-fg-faint">Период:</span>
          {periods(d?.today ?? new Date().toLocaleDateString("sv-SE")).map((p) => (
            <button key={p.label} type="button" onClick={() => set({ ...NO_PICK, from: p.from, to: p.to })} className={pill(from === p.from && to === p.to)}>
              {p.label}
            </button>
          ))}
          {d && (
            <span className="ml-2 text-[12px] text-fg-faint">
              {d.months[0]?.label} — {d.months.at(-1)?.label} · сверено с CRM {d.synced ? d.synced.replace("T", " ") : "ещё ни разу"}
            </span>
          )}
        </div>
        )}
      </div>
      {tab === "notice" && <Notice side={side} />}
      {tab !== "notice" && q.isPending && <p className="text-[12.5px] text-fg-muted">Загружаю…</p>}
      {tab !== "notice" && q.error && <p className="text-[12.5px] text-err">{String(q.error)}</p>}
      {tab !== "notice" && d && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-annot="невозвраты сейчас">
            <NowTile label="Невозврат сейчас" n={d.now.overdue} dress={d.dress} hint="на руках, срок проката вышел" active={isNow("open")} on={() => setPick(now("open", "невозврат сейчас"))} />
            <NowTile label="Из них старше 30 дней" n={d.now.age.more} dress={d.dress} hint={`до 3 дн. — ${d.now.age.d3.items}, 4–14 — ${d.now.age.d14.items}, 15–30 — ${d.now.age.d30.items}`} active={isNow("old")} on={() => setPick(now("old", "невозврат старше 30 дней"))} />
            <NowTile label="Сделка закрыта без возврата" n={d.now.closed} dress={d.dress} hint="сделка уже не в «Ожидаем возврат»" active={isNow("closed")} on={() => setPick(now("closed", "сделка закрыта без возврата"))} />
            <NowTile label="В прокате, срок не вышел" n={d.now.renting} dress={d.dress} hint={`без даты конца проката — ${d.now.no_date.items}`} active={isNow("renting")} on={() => setPick(now("renting", "в прокате, срок не вышел"))} />
          </div>
          <p className="text-[12px] text-fg-faint">
            Невозврат — вещь выдана по строке договора «прокат» (не продажа), возврата в CRM нет, конец проката по договору прошёл.
            Считаем {d.dress ? "только платья" : "все вещи: платья, аксессуары, мужские костюмы, обувь"}; плитки — на сегодня за всё время, таблицы — за
            выбранный период.
          </p>
          {tab === "months" ? (
            <>
              <Bars d={d} />
              <Months d={d} pick={shown} setPick={setPick} />
            </>
          ) : (
            <Cities d={d} metric={metric} setMetric={(m) => set({ metric: m === "open" ? undefined : m })} pick={shown} setPick={setPick} />
          )}
          {shown && <Deals pick={shown} all={all} side={side} />}
        </>
      )}
    </main>
  );
}
